import { Domain } from 'auth-common/domain';
import { Utils } from 'auth-common/shared';
import { throwIfAuthRequirementError } from '../../../utils/authRequirements';
import { apiClient } from '../../common/apiClient';
import { ApiConvereter } from '../../converter/index';
import type { IAuthRepository as I } from './interface';

import AccountGrant = Domain.Object.Account.Grant;

export class AuthRepository implements I {
  getInitialAuthInfo: I.GetInitialAuthInfo = async ({ signal }) => {
    const res = await apiClient.POST('/auth/get-initial-auth-info', { signal });
    throwIfAuthRequirementError(res.error);
    if (!res.data) throw new Error('authInfo not found');
    return ApiConvereter.AuthInfo.fromApi(res.data);
  };

  getBootstrap: I.GetBootstrap = async ({ projectId, signal }) => {
    const res = await apiClient.POST('/global-navi/get-bootstrap-v2', {
      signal,
      body: { ...(projectId ? { projectId } : {}) },
    });
    throwIfAuthRequirementError(res.error, { projectId });
    if (!res.data) throw new Error('bootstrap not found');
    return {
      authInfo: ApiConvereter.AuthInfo.fromApi(res.data.authInfo),
      // globalNavi はプラットフォーム非依存のデータ DTO のため converter 不要
      globalNavi: res.data.globalNavi,
    };
  };

  isAllowedIp: I.IsAllowedIp = async ({ projectId, signal }) => {
    const res = await apiClient.POST('/auth/is-allowed-ip', {
      signal,
      body: { projectId },
    });
    throwIfAuthRequirementError(res.error, { projectId });
    if (typeof res.data !== 'boolean') throw new Error('isAllowedIp not found');
    return res.data;
  };

  requestActiveGrant: I.RequestActiveGrant = async ({ grantKey, reason, clientConsent, firebaseIdToken, signal }) => {
    const res = await apiClient.POST('/auth/request-active-grant', {
      signal,
      body: { grantKey, reason, clientConsent, firebaseIdToken },
    });
    throwIfAuthRequirementError(res.error);
    if (!res.data) throw new Error('activeGrant not found');
    if (res.data.lifecycleState === AccountGrant.LifecycleState.Requested) {
      return {
        id: AccountGrant.Id.FromString(res.data.id),
        grantKey: res.data.grantKey,
        lifecycleState: AccountGrant.LifecycleState.Requested,
        reason: res.data.reason,
      };
    }
    if (!res.data.expiresAt) throw new Error('activeGrant expiresAt not found');
    return {
      id: AccountGrant.Id.FromString(res.data.id),
      grantKey: res.data.grantKey,
      lifecycleState: AccountGrant.LifecycleState.Active,
      expiresAt: Utils.Date.dayjs(res.data.expiresAt),
      reason: res.data.reason,
    };
  };

  getActiveGrantRequestStatus: I.GetActiveGrantRequestStatus = async ({ grantKey, signal }) => {
    const res = await apiClient.POST('/auth/get-active-grant-request-status', {
      signal,
      body: { grantKey },
    });
    throwIfAuthRequirementError(res.error);
    if (!res.data) throw new Error('activeGrantRequestStatus not found');
    if (res.data.lifecycleState === 'none') {
      return {
        grantKey: res.data.grantKey,
        lifecycleState: 'none',
      };
    }
    if (!res.data.id) throw new Error('activeGrantRequestStatus id not found');
    if (res.data.lifecycleState === AccountGrant.LifecycleState.Requested) {
      return {
        id: AccountGrant.Id.FromString(res.data.id),
        grantKey: res.data.grantKey,
        lifecycleState: AccountGrant.LifecycleState.Requested,
        reason: res.data.reason,
      };
    }
    if (res.data.lifecycleState === AccountGrant.LifecycleState.Expired) {
      return {
        id: AccountGrant.Id.FromString(res.data.id),
        grantKey: res.data.grantKey,
        lifecycleState: AccountGrant.LifecycleState.Expired,
        reason: res.data.reason,
      };
    }
    if (!res.data.expiresAt) throw new Error('activeGrantRequestStatus expiresAt not found');
    return {
      id: AccountGrant.Id.FromString(res.data.id),
      grantKey: res.data.grantKey,
      lifecycleState: AccountGrant.LifecycleState.Active,
      expiresAt: Utils.Date.dayjs(res.data.expiresAt),
      reason: res.data.reason,
    };
  };

  revokeActiveGrant: I.RevokeActiveGrant = async ({ grantKey, signal }) => {
    const res = await apiClient.POST('/auth/revoke-active-grant', {
      signal,
      body: { grantKey },
    });
    throwIfAuthRequirementError(res.error);
    if (!res.data) throw new Error('revokeActiveGrant not found');
    return {
      grantKey: res.data.grantKey,
    };
  };
}
